import { Box } from '@mui/material';
import { IconType } from 'componentregistry/icons/types';
import Icon from 'icons/Icon';
import React, { memo, MouseEventHandler } from 'react';
import ReactDOM from 'react-dom';
import { useSlate } from 'slate-react';
import { useTheme } from 'theme/Theme';
import { BlockType, MarkType } from './SlateTypes';
import {
  isBlockActive,
  toggleBlock,
  isMarkActive,
  toggleMark,
} from './SlateUtils';

export const Button = ({
  active,
  children,
  onMouseDown,
}: {
  active?: boolean;
  children: React.ReactNode;
  onMouseDown: MouseEventHandler<HTMLDivElement>;
}) => {
  const theme = useTheme();
  return (
    <Box
      onMouseDown={onMouseDown}
      sx={{
        cursor: 'pointer',
        bgcolor: active ? 'background.default' : 'background.paper',
        padding: 1,
        margin: 0,
        height: 32,
        borderRight: `1px solid ${theme.palette.divider}`,
      }}
    >
      {children}
    </Box>
  );
};

export const Portal: React.FC<{
  children: React.ReactNode;
}> = ({ children }: { children: React.ReactNode }) => {
  return ReactDOM.createPortal(children, document.body);
};

export const Toolbar: React.FC<{
  children: React.ReactNode;
}> = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        position: 'relative',
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        flexShrink: 0,
      }}
    >
      {children}
    </Box>
  );
};

export const BlockButton = ({
  format,
  icon,
  iconType,
}: {
  format: BlockType;
  icon: string;
  iconType?: IconType;
}) => {
  const editor = useSlate();
  //   useWhyDidYouUpdate('Btn', { editor, format, icon, iconType });
  return (
    <Button
      active={isBlockActive(editor, format)}
      onMouseDown={(event: React.MouseEvent) => {
        event.preventDefault();
        toggleBlock(editor, format);
      }}
    >
      <Icon iconName={icon} iconType={iconType} />
    </Button>
  );
};

export const MarkButton = ({
  format,
  icon,
}: {
  format: MarkType;
  icon: string;
}) => {
  const editor = useSlate();
  return (
    <Button
      active={isMarkActive(editor, format)}
      onMouseDown={(event: React.MouseEvent) => {
        event.preventDefault();
        toggleMark(editor, format);
      }}
    >
      <Icon iconName={icon} />
    </Button>
  );
};

export const Selection = () => {
  const editor = useSlate();
  return <>{JSON.stringify(editor.selection)}</>;
};

export const ToolB = memo(() => {
  return (
    <Toolbar>
      <MarkButton format="bold" icon="bold" />
      <MarkButton format="italic" icon="italic" />
      <MarkButton format="underline" icon="underline" />
      <MarkButton format="code" icon="code" />
      <BlockButton format="h1" icon="io-h1" iconType="cloudio" />
      <BlockButton format="h2" icon="io-h2" iconType="cloudio" />
      <BlockButton format="h3" icon="io-h3" iconType="cloudio" />
      <BlockButton format="h4" icon="io-h4" iconType="cloudio" />
      <BlockButton format="h5" icon="io-h5" iconType="cloudio" />
      <BlockButton format="h6" icon="io-h6" iconType="cloudio" />
      <BlockButton format="quote" icon="quote-left" />
      <BlockButton format="ol" icon="list-ol" />
      <BlockButton format="ul" icon="list-ul" />
    </Toolbar>
  );
});
